import AccessTimeIcon from '@material-ui/icons/AccessTime';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import React from 'react';

import type { WalletDetails } from '../../../contexts/app-state-context/web3-state/types';
import type { WalletProps } from '../../auction/card/WalletBalanceCard.component';
import { WalletBalanceCard } from '../../auction/card/WalletBalanceCard.component';
import { Tooltip } from '../../core/tooltip';
import { CountDown } from '../countdown/CountDown.component';
import styles from './CountdownCard.module.css';

export interface CountdownCardProps extends WalletProps {
  startTime: number;
  endTime: number;
  timerText: string;
  onlyTimerText: boolean;
  showTimer: boolean;
  toolTipText: string;
  getMoreStars?: () => void;
  walletInfo?: WalletDetails;
  countdownButtonReplacement?: JSX.Element;
  externalElements?: JSX.Element;
}

export const CountdownCard: React.FC<CountdownCardProps> = props => {
  const currentTime = Math.floor(Date.now() / 1000);
  const startDate = new Date(props.startTime * 1000);
  const endDate = new Date(props.endTime * 1000);
  const numDays = Math.floor((props.endTime - currentTime) / (60 * 60 * 24));

  const timer = props.onlyTimerText ? (
    <div
      className={
        'flex flex-row items-center justify-center absolute rounded-full h-8 ' +
        styles.countDown
      }
    >
      <AccessTimeIcon className="ml-2" />
      <div className="mx-1 font-noto-sans font-bold">{props.timerText}</div>
      {startDate.getTime() > Date.now() && (
        <div className="flex items-center">
          <div className="ml-1 font-noto-sans opacity-20"> | </div>
          <div className="ml-1">
            <CountDown
              date={startDate}
              numberSize="22px"
              days={false}
              fontFamily="NotoSans"
              displayFirstLetter={true}
              fontSize={false}
              delimiter=":"
            />
          </div>
          <Tooltip title={props.toolTipText} placement="top-end">
            <HelpOutlineIcon className="ml-1 mr-2 opacity-50" />
          </Tooltip>
        </div>
      )}
    </div>
  ) : Date.now() > startDate.getTime() && Date.now() < endDate.getTime() ? (
    <div
      className={
        'flex flex-row items-center justify-center absolute rounded-full h-8 ' +
        styles.countDown
      }
    >
      <AccessTimeIcon className="ml-2" />
      <div className="ml-1 font-noto-sans font-bold">
        {numDays} Day{numDays !== 1 && 's'}
      </div>
      <div className="ml-1 font-noto-sans opacity-20"> | </div>
      <div className="ml-1">
        <CountDown
          date={endDate}
          numberSize="22px"
          days={false}
          fontFamily="NotoSans"
          displayFirstLetter={true}
          fontSize={false}
          delimiter=":"
        />
      </div>
      <Tooltip title={props.toolTipText} placement="top-end">
        <HelpOutlineIcon className="ml-1 mr-2 opacity-50" />
      </Tooltip>
    </div>
  ) : (
    <div
      className={
        'flex flex-row items-center justify-center absolute rounded-full h-8 ' +
        styles.countDown
      }
    >
      <AccessTimeIcon className="ml-2" />
      <div className="ml-1 font-noto-sans font-bold mr-2">
        {props.timerText}
        {': '}
        {endDate.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </div>
    </div>
  );

  return (
    <div>
      <div
        className={
          props.walletInfo
            ? styles.container
            : styles.containerWithWallet + ' w-full relative z-10'
        }
      >
        <div className="relative flex flex-col items-center w-full bg-white bg-opacity-5 rounded-lg z-20">
          {props.showTimer && timer}
          <div className="w-full flex flex-col items-center h-auto">
            {props.children}
          </div>
        </div>
        {props.walletInfo && props.getMoreStars && (
          <div className={'relative w-full lg:h-full lg:top-0'}>
            <WalletBalanceCard
              transparent={true}
              getMoreStars={props.getMoreStars}
              buttonReplacement={props.countdownButtonReplacement}
              showAssets={props.showAssets}
            />
          </div>
        )}
      </div>
      {props.externalElements}
    </div>
  );
};
